// Items 26/27 — shared Excel-style column filter semantics for Table, Pivot and DataGrid.
// Filter shape: { values: [..] | null, op: string | null, opVal, opVal2 }

export const TEXT_OPS = [["eq", "Equals"], ["ne", "Not equals"], ["contains", "Contains"], ["ncontains", "Does not contain"], ["starts", "Starts with"], ["ends", "Ends with"], ["blank", "Is blank"], ["nblank", "Is not blank"]];
export const NUM_OPS = [["num_eq", "="], ["num_ne", "≠"], ["num_gt", ">"], ["num_gte", "≥"], ["num_lt", "<"], ["num_lte", "≤"], ["between", "Between"], ["top", "Top N"], ["bottom", "Bottom N"], ["blank", "Is blank"], ["nblank", "Is not blank"]];
export const DATE_OPS = [["date_on", "On"], ["date_before", "Before"], ["date_after", "After"], ["date_between", "Between"], ["blank", "Is blank"], ["nblank", "Is not blank"]];
export const NO_VALUE_OPS = new Set(["blank", "nblank"]);
export const TWO_VALUE_OPS = new Set(["between", "date_between"]);

export const isFilterActive = (ff) => !!ff && ((Array.isArray(ff.values) && ff.values.length > 0) || !!ff.op);

const dayOf = (v) => {
  if (v == null || v === "") return NaN;
  const d = new Date(v);
  return isNaN(d.getTime()) ? NaN : Math.floor(d.getTime() / 86400000);
};

export function matchesColumnFilter(ff, v) {
  if (!ff) return true;
  if (Array.isArray(ff.values) && ff.values.length && !ff.values.includes(String(v ?? "\u2014"))) return false;
  if (!ff.op || ff.op === "top" || ff.op === "bottom") return true;  // top/bottom handled at column level
  const s = String(v ?? "").toLowerCase();
  const q = String(ff.opVal ?? "").toLowerCase();
  const n = Number(v), qn = Number(ff.opVal), qn2 = Number(ff.opVal2);
  switch (ff.op) {
    case "eq": return s === q;
    case "ne": return s !== q;
    case "contains": return s.includes(q);
    case "ncontains": return !s.includes(q);
    case "starts": return s.startsWith(q);
    case "ends": return s.endsWith(q);
    case "blank": return v == null || v === "";
    case "nblank": return !(v == null || v === "");
    case "num_eq": return n === qn;
    case "num_ne": return n !== qn;
    case "num_gt": return n > qn;
    case "num_gte": return n >= qn;
    case "num_lt": return n < qn;
    case "num_lte": return n <= qn;
    case "between": return n >= Math.min(qn, qn2) && n <= Math.max(qn, qn2);
    case "date_on": return dayOf(v) === dayOf(ff.opVal);
    case "date_before": return dayOf(v) < dayOf(ff.opVal);
    case "date_after": return dayOf(v) > dayOf(ff.opVal);
    case "date_between": { const d = dayOf(v), a = dayOf(ff.opVal), b = dayOf(ff.opVal2); return d >= Math.min(a, b) && d <= Math.max(a, b); }
    default: return true;
  }
}

// Apply a map of {column: filter} to rows, including Top/Bottom N per column.
export function applyColumnFilters(rows, filters) {
  const ents = Object.entries(filters || {}).filter(([, ff]) => isFilterActive(ff));
  if (!ents.length) return rows;
  let out = rows.filter((r) => ents.every(([c, ff]) => matchesColumnFilter(ff, r[c])));
  ents.forEach(([c, ff]) => {
    if (ff.op !== "top" && ff.op !== "bottom") return;
    const n = Math.max(1, Number(ff.opVal) || 10);
    const ranked = [...out].filter((r) => Number.isFinite(Number(r[c]))).sort((a, b) => (ff.op === "top" ? Number(b[c]) - Number(a[c]) : Number(a[c]) - Number(b[c])));
    const keep = new Set(ranked.slice(0, n));
    out = out.filter((r) => keep.has(r));
  });
  return out;
}

export function detectColumnKind(values) {
  const vals = values.filter((v) => v != null && v !== "" && v !== "\u2014");
  if (!vals.length) return "text";
  if (vals.every((v) => Number.isFinite(Number(v)))) return "number";
  if (vals.every((v) => /^\d{4}-\d{2}-\d{2}/.test(String(v)) && !isNaN(new Date(v).getTime()))) return "date";
  return "text";
}

export function opsForKind(kind) {
  return kind === "number" ? NUM_OPS : kind === "date" ? DATE_OPS : TEXT_OPS;
}

export function describeFilter(ff, kind = "text") {
  if (!isFilterActive(ff)) return "";
  const parts = [];
  if (ff.values?.length) parts.push(ff.values.length === 1 ? ff.values[0] : `${ff.values.length} selected`);
  if (ff.op) {
    const label = opsForKind(kind).find(([v]) => v === ff.op)?.[1] || ff.op;
    parts.push(NO_VALUE_OPS.has(ff.op) ? label : TWO_VALUE_OPS.has(ff.op) ? `${label} ${ff.opVal}–${ff.opVal2}` : `${label} ${ff.opVal}`);
  }
  return parts.join(" · ");
}
